/**
 * Hooks for operator "what changed" version banner (Phase 23.2.7 — Living SOPs).
 *
 * Backed by:
 *  - GET  /api/operator/sops/:sopId/version-banner
 *  - POST /api/operator/sops/:sopId/version-banner/acknowledge
 *
 * Banner visibility is driven by the server (not localStorage) so dismissal
 * persists across sessions and devices. The server checks
 * operator_sop_acknowledgments for a row keyed by (operatorId, sopId,
 * currentVersionId); absence = banner shown.
 *
 * When a NEW version is published for the same SOP, the stored ack is for
 * the OLD versionId, so the visibility check returns no row and the banner
 * re-appears automatically.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// ── Response shape ──────────────────────────────────────────────────────────

export interface SopVersionBannerResponse {
  showBanner: boolean;
  versionId?: string;
  versionNumber?: string;
  changeSummary?: string | null;
  publishedAt?: string | null;
  firstChangedStepId?: string | null;
}

// ── Query key factory ───────────────────────────────────────────────────────

export const sopVersionBannerKeys = {
  all: ['sopVersionBanner'] as const,
  bySop: (sopId: string) => [...sopVersionBannerKeys.all, sopId] as const,
};

// ── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Fetch whether the current operator should see a "what changed" banner
 * for the given SOP. Returns { showBanner: false } for trainers/managers
 * without an operator profile — endpoint is a no-op in that case.
 */
export function useSopVersionBanner(sopId: string | undefined) {
  const api = useApi();
  return useQuery<SopVersionBannerResponse>({
    queryKey: sopId
      ? sopVersionBannerKeys.bySop(sopId)
      : sopVersionBannerKeys.all,
    queryFn: () =>
      api.get<SopVersionBannerResponse>(
        `/api/operator/sops/${sopId}/version-banner`
      ),
    enabled: !!sopId,
    // The banner is cheap to fetch and stable across the session until the
    // operator dismisses or a new version publishes — moderate staleTime is
    // fine here.
    staleTime: 30_000,
  });
}

/**
 * Dismiss the banner. Idempotent — POST is safe to fire on double-click.
 * Invalidates the banner query on success so the UI flips to hidden.
 */
export function useAcknowledgeSopVersion(sopId: string | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { acknowledged: boolean },
    Error,
    { versionId: string }
  >({
    mutationFn: (body) =>
      api.post<{ acknowledged: boolean }>(
        `/api/operator/sops/${sopId}/version-banner/acknowledge`,
        body
      ),
    onSuccess: () => {
      if (sopId) {
        queryClient.invalidateQueries({
          queryKey: sopVersionBannerKeys.bySop(sopId),
        });
      }
    },
  });
}
